"use client";

import { useTranslations } from "next-intl";
import { toast } from "@/components/ui/sonner";
import { useCartStore } from "@/features/cart/components/cart-provider";
import { usePendingRemovals } from "@/features/cart/components/pending-removals-provider";
import type { CartStore } from "@/features/cart/store/cart-store";
import type { Cart } from "@/features/cart/types/cart.type";
import { RemoveFromCartButton } from "./remove-from-cart-button";

export const RemoveFromCartForm = ({
  id,
  afterSubmit,
  className,
  type = "diet",
  variant = "outline",
  customButton,
}: {
  id?: string;
  afterSubmit?: () => void;
  className?: string;
  type?: "multidiet" | "diet";
  variant?: "outline" | "ghost";
  customButton?: (pending: boolean) => React.ReactNode;
}) => {
  const t = useTranslations();
  const cartStore: CartStore = useCartStore((state) => state);
  const { queue } = usePendingRemovals();

  const handleRemove = async () => {
    // A diet is only marked as removed here; the cart write happens once the
    // undo window closes (MPL-3899).
    if (type === "diet") {
      if (id) {
        queue({ kind: "diet", itemId: id });
      }

      afterSubmit?.();
      return;
    }

    await fetch("/api/cart/remove", {
      method: "POST",
      body: JSON.stringify({ cartId: cartStore.cartId, itemId: id, type }),
    })
      // TODO send event for multidiet
      .then((res: Response) => {
        if (!res.ok) {
          throw new Error("Failed to remove from cart");
        }

        return res.json() as Promise<{
          cartId: string;
          cart: Cart;
        }>;
      })
      .then(({ cartId, cart }) => {
        cartStore.updateCart(cartId, cart, "remove", t);
        if (id) cartStore.removeDeliveryCost(id);
      })
      .then(() => afterSubmit?.())
      .catch((error: Error) => toast.error(error.message));
  };

  return (
    <form action={handleRemove} className={className}>
      <RemoveFromCartButton customButton={customButton} variant={variant} />
    </form>
  );
};
